/**
 * Shared Beatly api-v2 base URL and JSON error parsing for app-facing HTTP clients
 * (beatlyApi.ts, canvaApi.ts, etc.).
 */

/**
 * Resolves `VITE_BEATLY_API_URL`. Empty string is allowed when the env var is set
 * (relative URLs / Vite dev proxy). When the var is unset, development falls back to localhost.
 */
export function getBeatlyApiBaseUrl(): string {
  const rawEnvValue = import.meta.env.VITE_BEATLY_API_URL;
  const apiUrl = typeof rawEnvValue === 'string' ? rawEnvValue.trim() : '';

  if (rawEnvValue !== undefined) {
    return apiUrl;
  }
  if (import.meta.env.MODE === 'development') {
    const fallback = 'http://localhost:8000';
    console.warn('[BeatlyApi] VITE_BEATLY_API_URL not set, using fallback:', fallback);
    return fallback;
  }
  throw new Error('Beatly API URL not configured. Set VITE_BEATLY_API_URL.');
}

/**
 * Resolve connectors-api origin for Tier-1 ad-accounts (and Tier-2 Connect).
 * Prefers ``VITE_CONNECTORS_API_URL``; falls back to api-connectors hosts derived
 * from the api-v2 URL when unset (staging vs production).
 */
export function getConnectorsApiBaseUrl(): string {
  const raw = import.meta.env.VITE_CONNECTORS_API_URL;
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim().replace(/\/$/, '');
  }
  if (import.meta.env.MODE === 'development') {
    return 'http://localhost:8080';
  }
  try {
    const beatly = getBeatlyApiBaseUrl();
    if (beatly.includes('staging')) {
      return 'https://api-connectors.staging.beatly.com';
    }
  } catch {
    // fall through
  }
  return 'https://api-connectors.beatly.com';
}

/**
 * Normalize FastAPI / app JSON error bodies for thrown errors.
 * Handles `detail` as string, `detail` as `{ code, message }`, and top-level `code`.
 */
export function beatlyApiErrorFromParsedBody(
  data: unknown,
  status: number,
  fallbackMessage: string
): { message: string; code?: string } {
  let code: string | undefined;
  if (typeof data === 'object' && data !== null && 'code' in data) {
    const c = (data as { code: unknown }).code;
    if (c != null && c !== '') code = String(c);
  }

  if (typeof data === 'object' && data !== null && 'detail' in data) {
    const detail = (data as { detail: unknown }).detail;
    if (typeof detail === 'string') {
      return { message: detail, code };
    }
    if (detail && typeof detail === 'object') {
      const d = detail as { code?: string; message?: string };
      const nestedCode = d.code != null && d.code !== '' ? String(d.code) : code;
      const message =
        d.message != null && String(d.message) !== '' ? String(d.message) : nestedCode ?? fallbackMessage;
      return { message, code: nestedCode };
    }
  }

  return { message: fallbackMessage, code };
}
